import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { UseMutationResult } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import type { DashboardMode, FilterPresetSurface } from "@nexus/contracts";
import { GhostTextCombobox } from "@nexus/ui";
import {
  readActivePresetId,
  writeActivePresetId,
} from "@/features/dashboard/lib/active-preset-storage";
import { useDeleteFilterPreset } from "@/lib/queries/filter-presets.queries";

export interface FilterPresetSelectorLabels {
  placeholder: string;
  triggerLabel: string;
  listHeading: string;
  searchPlaceholder: string;
  emptyMessage: string;
  listboxLabel: string;
  ariaLabel: string;
  deleteAria: string;
  saveHint?: string;
}

interface FilterPresetSelectorCoreProps<
  TPreset extends { id: string; name: string },
  TCreateInput extends { name: string },
> {
  surface: FilterPresetSurface;
  mode: DashboardMode;
  canListPresets: boolean;
  labels: FilterPresetSelectorLabels;
  filtersHydrated?: boolean;
  filterSignature: string;
  presets: TPreset[];
  createMutation?: UseMutationResult<TPreset, Error, TCreateInput>;
  buildPayload?: (mode: DashboardMode) => Record<string, unknown>;
  applyPreset: (preset: TPreset) => void;
  presetMatchesCurrent: (preset: TPreset) => boolean;
  onPresetApplied?: (preset: TPreset) => void;
  applySignal?: number;
  onPendingPresetChange?: (hasPending: boolean) => void;
  allowCreate?: boolean;
  selectPresetId?: string | null;
}

function filterPresetItems<TPreset extends { id: string; name: string }>(
  presets: TPreset[],
  query: string,
): TPreset[] {
  const trimmed = query.trim();
  if (!trimmed) return presets;
  const lower = trimmed.toLowerCase();
  const prefixMatches = presets.filter((preset) => preset.name.toLowerCase().startsWith(lower));
  if (prefixMatches.length > 0) return prefixMatches;
  return presets.filter((preset) => preset.name.toLowerCase().includes(lower));
}

function findExactPreset<TPreset extends { id: string; name: string }>(
  presets: TPreset[],
  query: string,
): TPreset | undefined {
  const lower = query.trim().toLowerCase();
  return presets.find((preset) => preset.name.toLowerCase() === lower);
}

function getGhostSuffix<TPreset extends { id: string; name: string }>(
  query: string,
  preset: TPreset | null,
): string | null {
  if (!preset) return null;
  const trimmed = query.trim();
  if (!trimmed) return null;
  if (!preset.name.toLowerCase().startsWith(trimmed.toLowerCase())) return null;
  return preset.name.slice(trimmed.length);
}

function resolveListHighlightId<TPreset extends { id: string; name: string }>(
  items: TPreset[],
  activePresetId: string | null,
): string | null {
  if (items.length === 0) return null;
  if (activePresetId && items.some((preset) => preset.id === activePresetId)) {
    return activePresetId;
  }
  return items[0]!.id;
}

export function FilterPresetSelectorCore<
  TPreset extends { id: string; name: string },
  TCreateInput extends { name: string },
>({
  surface,
  mode,
  canListPresets,
  labels,
  filtersHydrated = true,
  filterSignature,
  presets,
  createMutation,
  buildPayload,
  applyPreset,
  presetMatchesCurrent,
  onPresetApplied,
  applySignal,
  onPendingPresetChange,
  allowCreate = true,
  selectPresetId = null,
}: FilterPresetSelectorCoreProps<TPreset, TCreateInput>) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  const deleteMutation = useDeleteFilterPreset(surface, mode);

  const filteredPresets = useMemo(
    () => filterPresetItems(presets, inputValue),
    [presets, inputValue],
  );

  const highlightedPreset = useMemo(
    () => filteredPresets.find((preset) => preset.id === highlightedId) ?? filteredPresets[0] ?? null,
    [filteredPresets, highlightedId],
  );

  const ghostSuffix = useMemo(
    () => getGhostSuffix(inputValue, highlightedPreset),
    [inputValue, highlightedPreset],
  );

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        setHighlightedId(resolveListHighlightId(filterPresetItems(presets, inputValue), activePresetId));
      }
    },
    [activePresetId, inputValue, presets],
  );

  useLayoutEffect(() => {
    if (!open) return;
    setHighlightedId(resolveListHighlightId(filteredPresets, activePresetId));
  }, [activePresetId, filteredPresets, open]);

  useEffect(() => {
    if (!filtersHydrated || !activePresetId) return;
    const preset = presets.find((p) => p.id === activePresetId);
    if (!preset || !presetMatchesCurrent(preset)) {
      writeActivePresetId(surface, mode, null);
      setActivePresetId(null);
    }
  }, [activePresetId, filtersHydrated, presets, filterSignature, mode, presetMatchesCurrent, surface]);

  useEffect(() => {
    if (!filtersHydrated || activePresetId) return;
    const savedId = readActivePresetId(surface, mode);
    if (!savedId) return;
    const preset = presets.find((p) => p.id === savedId);
    if (preset && presetMatchesCurrent(preset)) {
      setActivePresetId(savedId);
    }
  }, [
    activePresetId,
    filtersHydrated,
    presets,
    filterSignature,
    mode,
    presetMatchesCurrent,
    surface,
  ]);

  const applyPresetFilters = useCallback(
    (preset: TPreset) => {
      applyPreset(preset);
      setActivePresetId(preset.id);
      setHighlightedId(preset.id);
      setInputValue("");
      setOpen(false);
      writeActivePresetId(surface, mode, preset.id);
      onPresetApplied?.(preset);
    },
    [applyPreset, mode, onPresetApplied, surface],
  );

  const handleEnter = useCallback(() => {
    const trimmed = inputValue.trim();
    const existing = findExactPreset(presets, trimmed);
    if (existing) {
      applyPresetFilters(existing);
      return;
    }
    if (highlightedPreset) applyPresetFilters(highlightedPreset);
  }, [applyPresetFilters, highlightedPreset, inputValue, presets]);

  const lastSelectPresetIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectPresetId || selectPresetId === lastSelectPresetIdRef.current) return;
    const preset = presets.find((p) => p.id === selectPresetId);
    if (!preset) return;
    lastSelectPresetIdRef.current = selectPresetId;
    setActivePresetId(selectPresetId);
    setHighlightedId(selectPresetId);
    setInputValue("");
    writeActivePresetId(surface, mode, selectPresetId);
  }, [mode, presets, selectPresetId, surface]);

  const handleApplySignal = useCallback(async () => {
    if (!allowCreate) return;
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (createMutation?.isPending) return;

    const existing = findExactPreset(presets, trimmed);
    if (existing) {
      applyPresetFilters(existing);
      return;
    }

    if (!allowCreate || !createMutation || !buildPayload) return;

    const payload = buildPayload(mode);
    const created = await createMutation.mutateAsync({
      name: trimmed,
      ...payload,
    } as TCreateInput);
    setActivePresetId(created.id);
    setHighlightedId(created.id);
    setInputValue("");
    setOpen(false);
    writeActivePresetId(surface, mode, created.id);
  }, [
    allowCreate,
    applyPresetFilters,
    buildPayload,
    createMutation,
    inputValue,
    mode,
    presets,
    surface,
  ]);

  const lastHandledSignalRef = useRef(applySignal);
  useEffect(() => {
    if (applySignal === undefined) return;
    if (lastHandledSignalRef.current === applySignal) return;
    lastHandledSignalRef.current = applySignal;
    void handleApplySignal();
  }, [applySignal, handleApplySignal]);

  const comboboxItems = useMemo(
    () => filteredPresets.map((preset) => ({ id: preset.id, label: preset.name })),
    [filteredPresets],
  );

  const trimmedInput = inputValue.trim();
  const hasExactMatch = Boolean(findExactPreset(presets, trimmedInput));
  const hasPendingPreset =
    allowCreate && canListPresets && Boolean(trimmedInput && !hasExactMatch);

  useEffect(() => {
    onPendingPresetChange?.(hasPendingPreset);
  }, [hasPendingPreset, onPendingPresetChange]);

  useEffect(() => {
    return () => {
      onPendingPresetChange?.(false);
    };
  }, [onPendingPresetChange]);

  const activeLabel = presets.find((preset) => preset.id === activePresetId)?.name;

  if (!canListPresets) return null;

  return (
    <GhostTextCombobox
      items={comboboxItems}
      inputValue={inputValue}
      onInputChange={(value) => {
        setInputValue(value);
        if (activePresetId) {
          writeActivePresetId(surface, mode, null);
          setActivePresetId(null);
        }
      }}
      highlightedId={highlightedId}
      onHighlightedIdChange={setHighlightedId}
      selectedId={activePresetId}
      ghostSuffix={ghostSuffix}
      onGhostAccept={() => {
        if (highlightedPreset) setInputValue(highlightedPreset.name);
      }}
      onSubmit={handleEnter}
      onSelectItem={(item) => {
        const preset = presets.find((p) => p.id === item.id);
        if (preset) applyPresetFilters(preset);
      }}
      onEscape={() => setHighlightedId(null)}
      open={open}
      onOpenChange={handleOpenChange}
      placeholder={activeLabel ?? labels.placeholder}
      triggerLabel={labels.triggerLabel}
      listHeading={labels.listHeading}
      searchPlaceholder={labels.searchPlaceholder}
      emptyMessage={labels.emptyMessage}
      listboxLabel={labels.listboxLabel}
      ariaLabel={labels.ariaLabel}
      saveHint={labels.saveHint}
      triggerClassName="max-w-[12rem]"
      contentClassName="w-72"
      renderItemActions={(item) => (
        <button
          type="button"
          className="ml-2 rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          aria-label={labels.deleteAria}
          onClick={(event) => {
            event.stopPropagation();
            void deleteMutation.mutateAsync(item.id);
            if (activePresetId === item.id) {
              writeActivePresetId(surface, mode, null);
              setActivePresetId(null);
              setInputValue("");
            }
          }}
        >
          <Trash2 className="size-3.5" aria-hidden />
        </button>
      )}
    />
  );
}
